import { Form, FormCancel } from "../components";
import { useFeedback } from "../hooks/useFeedback";
import PropTypes from "prop-types";
import { Toast, PageTitle, Card, FormSection, Btn, ConfirmModal, Input, Select, Modal, Spinner, Pagination } from "../components";
import { useState, useEffect } from "react";
import { apiFetch } from "../api";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { useTenantGeography } from "../hooks/useTenantGeography";
import { recordFormErrors } from "../observability";
import AddressAutocomplete from "./AddressAutocomplete";

function formatPhone(digits) {
  const d = (digits || "").replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

function phoneToDisplay(stored) {
  const d = (stored || "").replace(/\D/g, "");
  const local = d.startsWith("55") ? d.slice(2) : d;
  return formatPhone(local);
}

function phoneToStore(displayValue) {
  const d = (displayValue || "").replace(/\D/g, "").slice(0, 11);
  return d ? `55${d}` : "";
}

export default function MotoristasPage({ motoristas, setMotoristas, userRole }) {
  const isSuperadmin = userRole === "superadmin";
  const [form, setForm] = useState({ nome: "", telefone: "", rua: "", numero: "", bairro: "", cidade: "", cliente_id: "" });
  const { cidades: CIDADES } = useTenantGeography(isSuperadmin ? (form.cliente_id || null) : null);
  const [modal, setModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const { toast, toastType, showToast, showError, dismissToast } = useFeedback("motoristas");
  const [confirmId, setConfirmId] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [clientes, setClientes] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const PER_PAGE = 20;

  const isMobile = useMediaQuery("(max-width: 767px)");
  const isCompact = useMediaQuery("(max-width: 1023px)");

  useEffect(() => {
    if (!isSuperadmin) return;
    apiFetch("/clientes-rotaflex").then(setClientes).catch(() => { });
  }, [isSuperadmin]);



  const filtered = motoristas
    .filter(m =>
      m.nome.toLowerCase().includes(search.toLowerCase()) ||
      m.telefone?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };

  const openNew = () => {
    setSubmitError("");
    const f = { nome: "", telefone: "", rua: "", numero: "", bairro: "", cidade: "", cliente_id: "" };
    setForm(f); setEditItem(null); setFormErrors({}); setModal(true);
  };

  const openEdit = (m) => {
    setSubmitError("");
    const f = { ...m, cliente_id: m.cliente_id || "" };
    setForm(f); setEditItem(m.id); setFormErrors({}); setModal(true);
  };



  const handleAddressSelect = (detalhes) => {
    setForm(prev => ({
      ...prev,
      rua: detalhes.rua || prev.rua,
      numero: detalhes.numero || prev.numero,
      bairro: detalhes.bairro || prev.bairro,
      cidade: CIDADES.includes(detalhes.cidade) ? detalhes.cidade : prev.cidade,
    }));
  };

  const save = async () => {
    if (loading) return;
    setSubmitError("");
    const errors = {};
    if (!form.nome) errors.nome = "Nome é obrigatório.";
    if (isSuperadmin && !form.cliente_id) errors.cliente_id = "Selecione o cliente.";
    if (Object.keys(errors).length) {
      recordFormErrors(errors, "motoristas");
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setLoading(true);
    try {
      const body = { ...form };
      if (editItem) {
        const res = await apiFetch(`/motoristas/${editItem}`, { method: "PUT", body: JSON.stringify(body) });
        setMotoristas(motoristas.map(m => m.id === editItem ? res : m));
      } else {
        const res = await apiFetch("/motoristas", { method: "POST", body: JSON.stringify(body) });
        setMotoristas([...motoristas, res]);
      }
      setModal(false);
      showToast(editItem ? "Motorista atualizado." : "Motorista cadastrado.");
    } catch (e) { setSubmitError(e.message); showError(e.message); }
    finally { setLoading(false); }
  };

  const remove = async () => {
    try {
      await apiFetch(`/motoristas/${confirmId}`, { method: "DELETE" });
      setMotoristas(motoristas.filter(m => m.id !== confirmId));
      setConfirmId(null);
      showToast("Motorista excluído.");
    } catch (e) { showError(e.message); }
  };

  return (
    <div className="fade-in">
      {/* Header: empilha no mobile */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
        <div>
          <PageTitle icon="person">Motoristas</PageTitle>
          <p style={{ color: "var(--muted)", fontSize: '1rem', marginTop: 2 }}>{motoristas.length} motorista{motoristas.length === 1 ? "" : "s"} cadastrado{motoristas.length === 1 ? "" : "s"}</p>
        </div>
        <Btn onClick={openNew}>+ Novo motorista</Btn>
      </div>

      <Card surface="filter" style={{ marginBottom: 20 }}>
        <Input label="Buscar motorista" type="search" placeholder="Nome ou telefone" value={search} onChange={handleSearch} />
      </Card>

      {!isCompact && filtered.length > 0 ? (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table className="rf-data-table">
            <thead><tr><th scope="col">Motorista</th><th scope="col">Telefone</th><th scope="col">Endereço</th><th scope="col" style={{ textAlign: 'right' }}>Ações</th></tr></thead>
            <tbody>{paginated.map(m => <tr key={m.id}>
              <td><strong>{m.nome}</strong></td>
              <td style={{ whiteSpace: 'nowrap' }}>{m.telefone ? phoneToDisplay(m.telefone) : 'Não informado'}</td>
              <td style={{ color: 'var(--muted)' }}>{[m.rua, m.numero, m.bairro, m.cidade].filter(Boolean).join(', ') || 'Não informado'}</td>
              <td><div className="rf-row-actions"><Btn size="sm" variant="secondary" onClick={() => openEdit(m)}>Editar</Btn><Btn size="sm" variant="danger" onClick={() => setConfirmId(m.id)}>Excluir</Btn></div></td>
            </tr>)}</tbody>
          </table>
        </Card>
      ) : (
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 14 }}>
        {filtered.length === 0 && (
          <Card style={{ gridColumn: "1/-1", textAlign: "center", padding: 40 }}>
            <div style={{ color: "var(--muted)", marginBottom: 14 }}>
              {search ? `Nenhum resultado para "${search}"` : "Nenhum motorista cadastrado ainda"}
            </div>
            {search
              ? <Btn size="sm" variant="secondary" onClick={() => { setSearch(""); setPage(1); }}>Limpar busca</Btn>
              : <Btn size="sm" onClick={openNew}>+ Cadastrar motorista</Btn>
            }
          </Card>
        )}
        {paginated.map(m => (
          <Card key={m.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, overflowWrap: "anywhere" }}>
              <div>
                <div style={{ fontWeight: 600 }}>{m.nome}</div>
                <div style={{ fontSize: '0.875rem', color: "var(--muted)", fontVariantNumeric: "tabular-nums", marginTop: 2 }}>{m.telefone ? phoneToDisplay(m.telefone) : 'Telefone não informado'}</div>
                {m.rua && <div style={{ fontSize: '0.875rem', color: "var(--muted)", marginTop: 2 }}>{m.rua}{m.numero ? `, ${m.numero}` : ''}{m.bairro ? ` — ${m.bairro}` : ''}</div>}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <Btn size="sm" variant="secondary" onClick={() => openEdit(m)}>Editar</Btn>
              <Btn size="sm" variant="danger" onClick={() => setConfirmId(m.id)}>Excluir</Btn>
            </div>
          </Card>
        ))}
      </div>
      )}

      {filtered.length > PER_PAGE && (
        <Card style={{ padding: 0, marginTop: 8 }}>
          <Pagination page={page} total={filtered.length} perPage={PER_PAGE} onChange={setPage} />
        </Card>
      )}

      {modal && (
        <Modal title={editItem ? "Editar motorista" : "Novo motorista"} onClose={() => !loading && setModal(false)}>
          <Form values={form} onSubmit={save} busy={loading} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <p style={{ color: "var(--muted)", fontSize: '0.875rem' }}>Campos com * são obrigatórios.</p>
            <FormSection title="Identificação">
            <Input
              label="Nome *"
              value={form.nome}
              onChange={e => { setForm({ ...form, nome: e.target.value }); if (formErrors.nome) setFormErrors(p => ({ ...p, nome: "" })); }}
              placeholder="Carlos Souza"
              error={formErrors.nome}
            />
            {isSuperadmin && (
              <Select
                label="Cliente *"
                value={form.cliente_id}
                onChange={e => { setForm({ ...form, cliente_id: e.target.value }); if (formErrors.cliente_id) setFormErrors(p => ({ ...p, cliente_id: "" })); }}
                error={formErrors.cliente_id}
              >
                <option value="">Selecione o cliente</option>
                {clientes.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
              </Select>
            )}
            </FormSection>
            <FormSection title="Contato">
            <Input
              label="Telefone (WhatsApp)" type="tel" inputMode="tel"
              value={phoneToDisplay(form.telefone)}
              onChange={e => setForm({ ...form, telefone: phoneToStore(e.target.value) })}
              placeholder="(16) 99999-0000"
            />
            <span style={{ fontSize: '0.8125rem', color: "var(--muted)", marginTop: -8, display: "block" }}>
              Informe DDD e número, sem +55. O motorista recebe as rotas neste WhatsApp.
            </span>

            </FormSection>
            <FormSection title="Endereço do motorista">
            {/* Rua com autocomplete + Número: lado a lado no desktop, empilhados no mobile */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr auto", gap: 12, alignItems: "end" }}>
              <AddressAutocomplete
                label="Rua"
                value={form.rua}
                onChange={val => setForm({ ...form, rua: val })}
                onSelect={handleAddressSelect}
                placeholder="Digite a rua para buscar..."
              />
              <Input
                label="Número"
                value={form.numero || ""}
                onChange={e => setForm({ ...form, numero: e.target.value })}
                placeholder="100"
                style={{ width: isMobile ? "100%" : 80 }}
              />
            </div>

            <Input
              label="Bairro"
              value={form.bairro || ""}
              onChange={e => setForm({ ...form, bairro: e.target.value })}
              placeholder="Centro"
            />

            <Select label="Cidade" value={form.cidade || ""} onChange={e => setForm({ ...form, cidade: e.target.value })}>
              <option value="">Selecione a cidade</option>
              {CIDADES.map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
                {CIDADES.length === 0 && <p style={{ color: "var(--muted)", fontSize: '0.875rem' }}>Nenhuma cidade configurada. Peça ao administrador para revisar as cidades atendidas.</p>}

            </FormSection>
            {submitError && <p role="alert" style={{ color: "var(--danger)", fontSize: '0.9375rem', lineHeight: 1.5 }}>{submitError}</p>}
            <div className="rf-form-actions">
              <FormCancel disabled={loading} />
              <Btn type="submit" disabled={loading} >{loading ? <><Spinner /> Salvando...</> : "Salvar motorista"}</Btn>
            </div>
          </Form>
        </Modal>
      )}

      <ConfirmModal
        open={Boolean(confirmId)}
        title="Excluir motorista"
        message="Tem certeza? Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        confirmVariant="danger"
        onClose={() => setConfirmId(null)}
        onConfirm={remove}
      />



      {toast && !(modal && submitError) && (
        <Toast onDismiss={dismissToast} tone={toastType === "error" ? "error" : "success"}>{toast}</Toast>
      )}
    </div>
  );
}

MotoristasPage.propTypes = {
  motoristas: PropTypes.array.isRequired, setMotoristas: PropTypes.func.isRequired, userRole: PropTypes.string,
};
